/**
 * SEO keyword -> Shopify category handle (without the `amx-categories-` prefix)
 * for bike category pages, e.g. `/bikes/honda/cbr1000rr/2020/brake-pads`.
 *
 * The keyword is the URL segment and the page's canonical, so **changing a key
 * changes an indexed URL — add a 301 in the same MR**. Source of truth is the
 * keyword mapping sheet (OPG-2017); a category absent from here has no page and
 * stays a `categories` query filter.
 *
 * A Map, not an object literal, so a keyword like `constructor` can't resolve
 * to a prototype property.
 */
export const BIKE_CATEGORY_KEYWORD_HANDLES = new Map<string, string>(
  Object.entries({
    accessories: 'bike-accessories',
    'ramps-and-stands': 'bike-accessories-ramps-and-stands',
    'seat-covers': 'bike-accessories-seat-covers',
    'parts-and-maintenance': 'parts-and-maintenance',
    'bike-protection': 'parts-and-maintenance-accessories-bike-protection',
    // Shopify has six "Accessories>Bling" collections; only `-1` holds products.
    'bling-kits': 'parts-and-maintenance-accessories-bling-1',
    'hard-parts': 'parts-and-maintenance-hard-parts',
    'bearings-and-seals': 'parts-and-maintenance-hard-parts-bearings-and-seals',
    brakes: 'parts-and-maintenance-hard-parts-brakes',
    'brake-calipers': 'parts-and-maintenance-hard-parts-brakes-brake-calipers',
    'brake-discs': 'parts-and-maintenance-hard-parts-brakes-brake-discs',
    'brake-hoses': 'parts-and-maintenance-hard-parts-brakes-brake-hoses',
    'brake-pads': 'parts-and-maintenance-hard-parts-brakes-brake-pads',
    'brake-pedals': 'parts-and-maintenance-hard-parts-brakes-brake-pedals',
    'chains-and-sprockets': 'parts-and-maintenance-hard-parts-chain-and-sprockets',
    'chain-guides-and-sliders':
      'parts-and-maintenance-hard-parts-chain-and-sprockets-chain-guides-and-sliders',
    'chains-and-links': 'parts-and-maintenance-hard-parts-chain-and-sprockets-chains-and-links',
    sprockets: 'parts-and-maintenance-hard-parts-chain-and-sprockets-sprockets',
    'handlebar-and-controls': 'parts-and-maintenance-hard-parts-controls',
    'bar-clamps': 'parts-and-maintenance-hard-parts-controls-bar-clamps',
    'bar-ends': 'parts-and-maintenance-hard-parts-controls-bar-ends',
    'clutch-and-throttle-cables': 'parts-and-maintenance-hard-parts-controls-cables',
    footpegs: 'parts-and-maintenance-hard-parts-controls-footpegs',
    'gear-shifter': 'parts-and-maintenance-hard-parts-controls-gear-levers',
    handlebars: 'parts-and-maintenance-hard-parts-controls-handlebars',
    kickstarters: 'parts-and-maintenance-hard-parts-controls-kick-starters',
    'clutch-and-brake-levers': 'parts-and-maintenance-hard-parts-controls-levers',
    kickstand: 'parts-and-maintenance-hard-parts-controls-side-stands',
    'throttle-components': 'parts-and-maintenance-hard-parts-controls-throttle',
    electrical: 'parts-and-maintenance-hard-parts-electrical',
    headlights: 'parts-and-maintenance-hard-parts-electrical-headlights',
    indicators: 'parts-and-maintenance-hard-parts-electrical-indicators',
    switches: 'parts-and-maintenance-hard-parts-electrical-switches',
    'tail-lights': 'parts-and-maintenance-hard-parts-electrical-tail-lights',
    engine: 'parts-and-maintenance-hard-parts-engine',
    'clutch-sets': 'parts-and-maintenance-hard-parts-engine-clutch-sets',
    'top-end': 'parts-and-maintenance-hard-parts-engine-engine-top-end',
    'radiator-and-hoses': 'parts-and-maintenance-hard-parts-engine-radiator-and-hoses',
    exhaust: 'parts-and-maintenance-hard-parts-exhausts',
    'plastics-and-bike-protection': 'parts-and-maintenance-hard-parts-plastics-and-bike-protection',
    bashplates: 'parts-and-maintenance-hard-parts-plastics-and-bike-protection-bashplates',
    'disc-guards': 'parts-and-maintenance-hard-parts-plastics-and-bike-protection-disc-guards',
    'frame-guards': 'parts-and-maintenance-hard-parts-plastics-and-bike-protection-frame-guards',
    'hand-guards': 'parts-and-maintenance-hard-parts-plastics-and-bike-protection-hand-guards',
    suspension: 'parts-and-maintenance-hard-parts-suspension',
    'fork-accessories': 'parts-and-maintenance-hard-parts-suspension-fork-accessories',
    'fork-seals': 'parts-and-maintenance-hard-parts-suspension-fork-seals',
    'suspension-components': 'parts-and-maintenance-hard-parts-suspension-suspension-components',
    'swing-arms': 'parts-and-maintenance-hard-parts-suspension-swing-arms',
    'wheels-and-tyres': 'parts-and-maintenance-hard-parts-wheels-and-tyres',
    'adventure-tyres': 'parts-and-maintenance-hard-parts-wheels-and-tyres-adventure-tyres',
    'off-road-tyres': 'parts-and-maintenance-hard-parts-wheels-and-tyres-off-road-tyres',
    'road-tyres': 'parts-and-maintenance-hard-parts-wheels-and-tyres-road-tyres',
    'tyre-accessories': 'parts-and-maintenance-hard-parts-wheels-and-tyres-tyre-accessories',
    'tyre-tubes': 'parts-and-maintenance-hard-parts-wheels-and-tyres-tyre-tubes',
    'wheel-bearings': 'parts-and-maintenance-hard-parts-wheels-and-tyres-wheel-bearings',
    wheels: 'parts-and-maintenance-hard-parts-wheels-and-tyres-wheels',
    batteries: 'parts-and-maintenance-maintenance-batteries',
    'cleaning-and-detailing': 'parts-and-maintenance-maintenance-cleaning-and-detailing',
    'cleaning-tools':
      'parts-and-maintenance-maintenance-cleaning-and-detailing-cleaning-applicators',
    'cleaning-products':
      'parts-and-maintenance-maintenance-cleaning-and-detailing-cleaning-products',
    filters: 'parts-and-maintenance-maintenance-filters',
    'air-filters': 'parts-and-maintenance-maintenance-filters-air-filters',
    'oil-filters': 'parts-and-maintenance-maintenance-filters-oil-filters',
    'nuts-and-bolts': 'parts-and-maintenance-maintenance-nuts-and-bolts',
    'oils-and-fluids': 'parts-and-maintenance-maintenance-oils-and-fluids',
    'air-filter-oil': 'parts-and-maintenance-maintenance-oils-and-fluids-air-filter-oil',
    'brake-fluid': 'parts-and-maintenance-maintenance-oils-and-fluids-brake-fluid',
    coolant: 'parts-and-maintenance-maintenance-oils-and-fluids-coolant',
    'engine-oil': 'parts-and-maintenance-maintenance-oils-and-fluids-engine-oil',
    'fork-oil': 'parts-and-maintenance-maintenance-oils-and-fluids-fork-oil',
    lubricant: 'parts-and-maintenance-maintenance-oils-and-fluids-lubricant',
    'spark-plugs': 'parts-and-maintenance-maintenance-spark-plugs',
    'tool-kit': 'parts-and-maintenance-tools',
  }),
);

/** Reverse of {@link BIKE_CATEGORY_KEYWORD_HANDLES}, for building category links. */
export const BIKE_CATEGORY_HANDLE_KEYWORDS = new Map<string, string>(
  [...BIKE_CATEGORY_KEYWORD_HANDLES].map(([keyword, handle]) => [handle, keyword]),
);
